/* eslint-disable react/no-unstable-nested-components */
import { TextInput } from "lib/miolo";
import startCase from "lodash/fp/startCase";
import type { ReactElement } from "react";
import { useField, useForm } from "react-final-form-hooks";
import { object, string } from "yup";
import { Button } from "components/ui/Button";
import type { IntlShape } from "react-intl";
import { FormattedMessage, useIntl } from "react-intl";
import checkCanSubmit from "../../lib/utils/form/checkCanSubmit";
import validateFormValuesAgainstSchema from "../../lib/utils/form/validateFormValuesAgainstSchema";
import InlineErrorSpan from "../shared/form/InlineErrorSpan";

interface ISignUpFormValues {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

interface ISignUpFormProps {
  onSubmit: (values: ISignUpFormValues) => any | Promise<any>;
}

export default function SignUpForm({ onSubmit }: ISignUpFormProps): ReactElement {
  const intl = useIntl();

  const { form, handleSubmit, submitError, submitSucceeded, ...props } = useForm({
    onSubmit: (values: any) => {
      return onSubmit({
        ...values,
        firstName: startCase(values.firstName.trim()),
        lastName: startCase(values.lastName.trim()),
      });
    },
    validate: validateFormValuesAgainstSchema(getSignUpFormDataShape(intl)),
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
    },
  });

  const { submitting } = props;
  const canSubmit = checkCanSubmit(props);

  const firstName = useField("firstName", form);
  const lastName = useField("lastName", form);
  const email = useField("email", form);
  const password = useField("password", form);

  return (
    <form onSubmit={handleSubmit} autoComplete="off" className="grid gap-y-5">
      <TextInput
        {...firstName.input}
        height={44}
        type="text"
        spellCheck={false}
        autoComplete="off"
        placeholder={intl.formatMessage({
          defaultMessage: "Nome",
          id: "CH/6DN",
        })}
        name="firstName"
        maxLength={36}
        className="capitalize"
      />

      {firstName.meta.touched && firstName.meta.error && <InlineErrorSpan>{firstName.meta.error}</InlineErrorSpan>}

      <TextInput
        {...lastName.input}
        height={44}
        type="text"
        spellCheck={false}
        autoComplete="off"
        placeholder={intl.formatMessage({
          defaultMessage: "Sobrenome",
          id: "lLV5Ne",
        })}
        name="lastName"
        maxLength={36}
        className="capitalize"
      />

      {lastName.meta.touched && lastName.meta.error && <InlineErrorSpan>{lastName.meta.error}</InlineErrorSpan>}

      <TextInput
        {...email.input}
        height={44}
        type="email"
        spellCheck={false}
        autoComplete="off"
        placeholder="Email"
        name="email"
        maxLength={256}
      />

      {email.meta.touched && email.meta.error && <InlineErrorSpan>{email.meta.error}</InlineErrorSpan>}

      {isHotmailOrOutlook(email.input.value || "") && (
        <div className="-mx-6 bg-blue-50 px-6 py-3">
          <p className="m-0 text-center text-sm font-medium tracking-wide text-blue-600">
            <FormattedMessage
              defaultMessage={`<strong>Atenção!</strong> Este servidor de e-mail costuma ter problemas para receber a confirmação de cadastro, recomendamos que utilize um Gmail e clique em "Logar com o Google" para acesso imediato.`}
              id="1MH86u"
              description="Sign up form warning message for hotmail and outlook users"
              values={{
                strong: (text) => <strong>{text}</strong>,
              }}
            />
          </p>
        </div>
      )}

      {isGmail(email.input.value || "") && (
        <div className="-mx-6 bg-blue-50 px-6 py-3">
          <FormattedMessage
            defaultMessage="<strong>Atenção!</strong> Como você possui um Gmail, cadastre-se com a conta Google que não será necessário confirmar o e-mail e nem criar uma senha de acesso."
            id="+ZerjW"
            description="Sign up form warning message for Gmail users"
            values={{
              strong: (text) => <strong>{text}</strong>,
            }}
          />
        </div>
      )}

      <TextInput
        {...password.input}
        height={44}
        type="password"
        spellCheck={false}
        autoComplete="off"
        placeholder={intl.formatMessage({
          defaultMessage: "Senha",
          id: "yUZJVn",
        })}
        name="password"
        maxLength={256}
      />

      {password.meta.touched && password.meta.error && <InlineErrorSpan>{password.meta.error}</InlineErrorSpan>}

      <Button
        type="submit"
        disabled={!canSubmit}
        variant="default"
        className="border border-blue-600 bg-white text-blue-600 hover:bg-blue-600 hover:text-white"
      >
        {submitting ? (
          <FormattedMessage defaultMessage="Aguarde..." id="VZazm7" />
        ) : (
          <FormattedMessage defaultMessage="Quero me cadastrar" description="Sign up page" id="/czduW" />
        )}
      </Button>

      {submitError && (
        <div className="-mx-6 bg-red-100 px-6 py-3">
          <p className="m-0 text-center text-sm font-medium tracking-wide text-red-700">{submitError}</p>
        </div>
      )}

      {submitSucceeded && (
        <div className="-mx-6 bg-green-100 px-6 py-3">
          <p className="m-0 text-center text-sm font-medium tracking-wide text-green-700" />
          <FormattedMessage
            defaultMessage="<strong>Tudo certo!</strong> Seu cadastro foi realizado e seu login será efetuado automaticamente."
            id="e14aa8"
            description="Sign up page"
            values={{
              strong: (text) => <strong>{text}</strong>,
            }}
          />
        </div>
      )}
    </form>
  );
}

function isHotmailOrOutlook(email: string) {
  const lowerEmail = email.toLowerCase();

  return ["hotmail.com", "hotmail.com.br", "outlook.com", "outlook.com.br"].some((domain) => {
    return lowerEmail.endsWith(domain);
  });
}
function isGmail(email: string) {
  const lowerEmail = email.toLowerCase();
  return lowerEmail.endsWith("gmail.com");
}

function getSignUpFormDataShape(intl: IntlShape) {
  return object().shape({
    firstName: string()
      .trim()
      .required(
        intl.formatMessage({
          defaultMessage: "Seu nome é obrigatório",
          description: "Sign up form",
          id: "QSgzF9",
        }),
      )
      .min(
        2,
        intl.formatMessage({
          defaultMessage: "O nome é muito curto",
          description: "Sign up form",
          id: "mTVJrP",
        }),
      )
      .max(
        36,
        intl.formatMessage({
          defaultMessage: "Seu sobrenome deve ter no máximo 36 caracteres",
          description: "Sign up form",
          id: "ZFIwSG",
        }),
      ),
    lastName: string()
      .trim()
      .required(
        intl.formatMessage({
          defaultMessage: "Seu sobrenome é obrigatório",
          description: "Sign up form",
          id: "Qpbdzf",
        }),
      )
      .min(
        2,
        intl.formatMessage({
          defaultMessage: "Seu sobrenome é muito curto",
          description: "Sign up form",
          id: "2R0PPA",
        }),
      )
      .max(
        36,
        intl.formatMessage({
          defaultMessage: "Seu sobrenome deve ter no máximo 36 caracteres",
          description: "Sign up form",
          id: "ZFIwSG",
        }),
      ),
    email: string()
      .trim()
      .required(
        intl.formatMessage({
          defaultMessage: "Seu email é obrigatório",
          description: "Sign up form",
          id: "ufraAl",
        }),
      )
      .email(
        intl.formatMessage({
          defaultMessage: "O email fornecido é inválido",
          description: "Sign up form",
          id: "5FUNuC",
        }),
      ),
    password: string()
      .trim()
      .required(
        intl.formatMessage({
          defaultMessage: "Você deve definir uma senha",
          description: "Sign up form",
          id: "8umz1A",
        }),
      )
      .min(
        8,
        intl.formatMessage({
          defaultMessage: "A senha deve ter no mínimo 8 dígitos",
          description: "Sign up form",
          id: "UHILBl",
        }),
      ),
  });
}
